import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSystemPlans } from "@/hooks/use-plans";
import { Navigation } from "@/components/ui/navigation";
import { Footer } from "@/components/footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { TrackingPlanCard } from "@/components/gps/TrackingPlanCard";
import { TrackingLiveDemo } from "@/components/gps/TrackingLiveDemo";
import { TrackingSubscriptionModal } from "@/components/gps/TrackingSubscriptionModal";
import { TrackingTrialModal } from "@/components/gps/TrackingTrialModal";
import { TrackingDemoModal } from "@/components/gps/TrackingDemoModal";
import {
  MapPin,
  Route,
  Shield,
  AlertTriangle,
  FileText,
  Power,
  Signal,
  Truck,
  Code,
  CheckCircle,
  Navigation as NavigationIcon,
  ArrowRight,
  Zap,
  Clock,
  Users,
  TrendingUp,
  Activity
} from "lucide-react";

const trackingFeatures = [
  {
    icon: MapPin,
    title: "Mapas ao Vivo",
    description: "Rastreamento em tempo real com precisão GPS de alta qualidade e atualização contínua"
  },
  {
    icon: Route,
    title: "Histórico de Viagens",
    description: "Acesse todo o histórico de rotas com relatórios detalhados e análise de percursos"
  },
  {
    icon: Shield,
    title: "GeoCercas Inteligentes",
    description: "Crie perímetros virtuais e receba alertas automáticos de entrada e saída"
  },
  {
    icon: AlertTriangle,
    title: "Alertas de Velocidade",
    description: "Monitoramento em tempo real e notificações instantâneas de excesso de velocidade"
  },
  {
    icon: FileText,
    title: "Relatórios Avançados",
    description: "Analytics completo com exportação e API para integração com seu BI"
  },
  {
    icon: Power,
    title: "Comandos Remotos",
    description: "Controle remoto seguro para bloqueio e desbloqueio de veículos"
  },
  {
    icon: Signal,
    title: "Chips M2M Multi-Operadora",
    description: "Conectividade contínua com failover automático entre múltiplas redes"
  },
  {
    icon: Truck,
    title: "Integração FleetONE",
    description: "Sincronização bidirecional completa com gestão de frotas"
  },
  {
    icon: Code,
    title: "API & Webhooks",
    description: "Integre com seus sistemas via API REST e webhooks em tempo real"
  }
];

const fleetIntegrationBenefits = [
  "Sincronização automática de veículos e motoristas",
  "Alertas de manutenção baseados em quilometragem real",
  "Relatórios unificados de custos e performance",
  "Gestão centralizada de documentos e CNH",
  "Histórico completo de viagens e abastecimentos",
  "Analytics combinado para decisões estratégicas"
];

const stats = [
  { value: "99.9%", label: "Uptime Garantido" },
  { value: "5000+", label: "Veículos Rastreados" },
  { value: "<1s", label: "Latência Média" },
  { value: "24/7", label: "Suporte Técnico" }
];

const faqs = [
  {
    question: "Como funciona o comando remoto para desligar um veículo?",
    answer: "O recurso envia um comando seguro ao rastreador homologado instalado no veículo. Ele exige permissões elevadas, gera logs de auditoria completos e está em conformidade com as políticas da empresa. A disponibilidade depende do modelo do hardware e do perfil do plano contratado."
  },
  {
    question: "Os chips M2M são realmente multi-operadora?",
    answer: "Sim. Utilizamos SIM cards com múltiplos perfis de operadora e failover automático entre redes parceiras (Vivo, Claro, TIM) para manter a conectividade contínua mesmo em áreas de cobertura limitada."
  },
  {
    question: "Posso integrar os dados do rastreamento ao meu BI?",
    answer: "Sim. Além dos relatórios prontos no sistema, disponibilizamos API REST completa, webhooks para eventos em tempo real e exportações programáveis para integração com seu data warehouse ou ferramentas de BI."
  },
  {
    question: "Como é feita a integração com o FleetONE?",
    answer: "A integração é nativa e bidirecional: veículos, motoristas e eventos de GPS podem alimentar automaticamente dados de manutenção, custos e alertas do FleetONE, e vice-versa. Tudo sincronizado em tempo real."
  },
  {
    question: "Qual a precisão do rastreamento GPS?",
    answer: "Utilizamos tecnologia GPS/GNSS de alta precisão com margem de erro inferior a 10 metros em áreas abertas. Em túneis e áreas cobertas, o sistema utiliza triangulação de antenas celulares como backup."
  }
];

export default function TrackingOneLanding() {
  const { data: plans, isLoading: plansLoading } = useSystemPlans('gps_tracking');
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b1220] via-[#0f1829] to-[#0b1220]">
      <Navigation />
      <WhatsAppFloat systemName="TrackingONE" systemCode="gps_tracking" />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#0d9488]/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#a3e635]/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-[#0d9488]/20 text-[#a3e635] border-[#0d9488]/30 hover:bg-[#0d9488]/30">
              <NavigationIcon className="w-4 h-4 mr-2 animate-pulse" />
              Rastreamento Profissional GPS
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Rastreie sua frota em{" "}
              <span className="bg-gradient-to-r from-[#0d9488] to-[#a3e635] bg-clip-text text-transparent">
                tempo real
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/70 mb-10 leading-relaxed">
              Mapas ao vivo, geocercas inteligentes, comandos remotos e chips M2M multi-operadora. 
              Controle total da sua operação com tecnologia de ponta.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                size="lg" 
                className="bg-[#0d9488] hover:bg-[#0f766e] text-white text-lg px-8 py-6"
                onClick={() => setTrialOpen(true)}
              >
                <Zap className="mr-2 h-5 w-5" />
                Iniciar Teste Grátis
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-[#0d9488] text-[#0d9488] hover:bg-[#0d9488]/10 text-lg px-8 py-6"
                onClick={() => setDemoOpen(true)}
              >
                <Activity className="mr-2 h-5 w-5" />
                Ver Demonstração
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-4 rounded-lg bg-white/5 backdrop-blur border border-white/10">
                  <div className="text-3xl md:text-4xl font-bold text-[#a3e635] mb-1">{stat.value}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Rastreamento em <span className="text-[#a3e635]">Tempo Real</span>
            </h2>
            <p className="text-xl text-white/70">
              Veja sua frota no mapa com atualização instantânea
            </p>
          </div>
          <TrackingLiveDemo />
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent via-[#0d9488]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Recursos <span className="text-[#a3e635]">Completos</span>
            </h2>
            <p className="text-xl text-white/70">
              Tudo que você precisa para gestão profissional de rastreamento
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {trackingFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group p-6 rounded-xl bg-white/5 backdrop-blur border border-white/10 hover:border-[#0d9488]/50 hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#0d9488]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-[#a3e635]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/70">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FleetONE Integration */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl bg-gradient-to-br from-[#0d9488]/20 to-[#0f766e]/20 border border-[#0d9488]/30 p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="mb-4 bg-[#0d9488]/30 text-white border-[#0d9488]/50">
                  <Truck className="w-4 h-4 mr-2" />
                  Integração Nativa
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Integração Total com{" "}
                  <span className="text-[#a3e635]">FleetONE</span>
                </h2>
                <p className="text-white/80 text-lg mb-8">
                  Conecte o rastreamento GPS com a gestão completa da sua frota. 
                  Dados sincronizados em tempo real para decisões mais inteligentes.
                </p>
                <Button 
                  size="lg" 
                  className="bg-white text-[#0d9488] hover:bg-white/90"
                  asChild
                >
                  <Link to="/systems/fleetone">
                    Conhecer FleetONE
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>

              <div className="space-y-4">
                {fleetIntegrationBenefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-4 rounded-lg bg-white/5 backdrop-blur border border-white/10"
                  >
                    <CheckCircle className="w-6 h-6 text-[#a3e635] flex-shrink-0 mt-0.5" />
                    <span className="text-white/90">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="plans" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Planos para <span className="text-[#a3e635]">todas</span> as frotas
            </h2>
            <p className="text-xl text-white/70 mb-6">
              Escolha o plano ideal para o tamanho da sua operação
            </p>
            <Badge className="bg-white/10 text-white/70 border-white/20">
              Gerencie seus planos no PortalONE Admin
            </Badge>
          </div>

          {plansLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#0d9488] border-t-transparent" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {plans?.map((plan) => (
                <TrackingPlanCard 
                  key={plan.id} 
                  plan={plan}
                  onSubscribe={() => setSubscriptionOpen(true)}
                  onTrial={() => setTrialOpen(true)}
                />
              ))}
            </div>
          )}

          <div className="text-center">
            <Button 
              size="lg" 
              className="bg-[#0d9488] hover:bg-[#0f766e] text-lg px-8"
              asChild
            >
              <Link to="/admin/systems">
                Ver Todos os Planos
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-xl text-white/70">
              Dúvidas comuns sobre o TrackingONE
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group rounded-xl bg-white/5 backdrop-blur border border-white/10 overflow-hidden"
              >
                <summary className="cursor-pointer list-none px-6 py-5 text-white font-medium flex items-center justify-between hover:bg-white/5">
                  <span>{faq.question}</span>
                  <ArrowRight className="w-5 h-5 text-[#a3e635] group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <div className="px-6 pb-5 text-white/80 border-t border-white/10 pt-4">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Sticky */}
      <div className="sticky bottom-4 z-40 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl border border-white/10 bg-[#0b1220]/90 backdrop-blur-xl p-4 md:p-6 shadow-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-white/90 text-sm md:text-base">
                Pronto para elevar sua operação com rastreamento ao vivo, geocercas e comandos remotos?
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <Button 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/5"
                  onClick={() => setDemoOpen(true)}
                >
                  Ver Demo
                </Button>
                <Button 
                  className="bg-[#0d9488] hover:bg-[#0f766e]"
                  onClick={() => setTrialOpen(true)}
                >
                  Começar Agora
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Modals */}
      <TrackingSubscriptionModal open={subscriptionOpen} onOpenChange={setSubscriptionOpen} />
      <TrackingTrialModal open={trialOpen} onOpenChange={setTrialOpen} />
      <TrackingDemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
